import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useForm } from "react-hook-form";
import { Card, CardContent } from "../ui/card";
import { Form, FormField, FormLabel, FormInput, Button, FormMessage, Label, Switch } from "../ui";
import { View, Text } from "react-native";
import { trpc } from "@/trpc/react";
import Toast from "react-native-toast-message";
import { P } from "../ui/typography";
import { z } from "zod";
import { isPast } from "date-fns";

const finishedEarlyFormSchema = z.object({
  finished_early_available: z.boolean(),
  finished_early_hours: z.string().min(1, "Hours worked is required"),
  everyone_else_finished_early: z.boolean().optional(),
});

type FinishedEarlyFormValues = z.infer<typeof finishedEarlyFormSchema>;

interface FinishedEarlyFormProps {
  jobCrewId: number;
  scheduledHours: number;
  shiftEndTime: Date | null;
  isChief?: boolean;
}

export default function FinishedEarlyForm({ jobCrewId, scheduledHours, shiftEndTime, isChief = false }: FinishedEarlyFormProps) {
  const router = useRouter();

  // Check if shift has ended
  const isShiftEnded = shiftEndTime ? isPast(shiftEndTime) : false;

  const form = useForm<FinishedEarlyFormValues>({
    resolver: zodResolver(finishedEarlyFormSchema),
    defaultValues: {
      finished_early_available: false,
      finished_early_hours: "",
      everyone_else_finished_early: false,
    },
  });

  const { mutate: updateFinishedEarly, isPending } = trpc.finishedEarly.update.useMutation({
    onSuccess(_, variables) {
      router.back();
      const message = variables.everyone_else_finished_early
        ? 'Finished early information submitted! All crew members have been updated.'
        : 'Finished early information submitted!';
      Toast.show({
        type: 'success',
        text1: message,
        text2: 'Your information has been recorded.'
      });
    },
    onError(error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to submit',
        text2: error.message
      });
    }
  });

  async function onSubmit(data: FinishedEarlyFormValues) {
    const hoursValue = parseFloat(data.finished_early_hours);

    if (isNaN(hoursValue) || hoursValue <= 0) {
      Toast.show({
        type: 'error',
        text1: 'Invalid hours',
        text2: 'Please enter a valid number of hours worked.'
      });
      return;
    }

    updateFinishedEarly({
      job_crew_id: jobCrewId,
      // If shift has ended, always set availability to false
      finished_early_available: isShiftEnded ? false : data.finished_early_available,
      finished_early_hours: hoursValue,
      everyone_else_finished_early: isChief ? data.everyone_else_finished_early : undefined,
    });
  }

  return (
    <Form {...form}>
      <View className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <P className="text-sm text-blue-900 dark:text-blue-100">
          <Text className="font-semibold">Important: </Text>
          This won't affect your pay, it just helps us manage health and safety and gives you the opportunity to be eligible for more work, should you want it.
        </P>
      </View>
      <Card className='mt-4 mb-4'>
        <CardContent className="gap-6">
          <View className="gap-4">
            {!isShiftEnded && (
              <View className='flex flex-row items-center justify-between py-3 border-b border-border'>
                <FormLabel className="flex-1">Are you available for work immediately?</FormLabel>
                <FormField
                  control={form.control}
                  name="finished_early_available"
                  render={({ field }) => (
                    <View className='flex-row items-center gap-2'>
                      <Label
                        nativeID='finished_early_available'
                        onPress={() => {
                          field.onChange(!field.value);
                        }}
                      >
                        No
                      </Label>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        nativeID='finished_early_available'
                      />
                      <Label
                        nativeID='finished_early_available'
                        onPress={() => {
                          field.onChange(!field.value);
                        }}
                      >
                        Yes
                      </Label>
                    </View>
                  )}
                />
              </View>
            )}

            {isChief && !isShiftEnded && (
              <View className='flex flex-row items-center justify-between py-3 border-b border-border'>
                <FormLabel className="flex-1">Did everyone else finish early too?</FormLabel>
                <FormField
                  control={form.control}
                  name="everyone_else_finished_early"
                  render={({ field }) => (
                    <View className='flex-row items-center gap-2'>
                      <Label
                        nativeID='everyone_else_finished_early'
                        onPress={() => {
                          field.onChange(!field.value);
                        }}
                      >
                        No
                      </Label>
                      <Switch
                        checked={field.value || false}
                        onCheckedChange={field.onChange}
                        nativeID='everyone_else_finished_early'
                      />
                      <Label
                        nativeID='everyone_else_finished_early'
                        onPress={() => {
                          field.onChange(!field.value);
                        }}
                      >
                        Yes
                      </Label>
                    </View>
                  )}
                />
              </View>
            )}

            <FormField
              control={form.control}
              name="finished_early_hours"
              render={({ field }) => (
                <View className="gap-2 mt-4">
                  <FormLabel>Hours actually worked</FormLabel>
                  <P className="text-xs text-muted-foreground">
                    Scheduled hours: {scheduledHours}
                  </P>
                  <FormInput
                    label=""
                    keyboardType="decimal-pad"
                    placeholder="Enter hours worked"
                    {...field}
                    value={field.value || ''}
                    onChangeText={(value) => {
                      // Only allow valid decimal input
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        field.onChange(value);
                      }
                    }}
                  />
                </View>
              )}
            />
          </View>

          <View className="flex-row gap-3 mt-6">
            <Button
              onPress={() => router.back()}
              className="flex-1"
              variant="outline"
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              onPress={form.handleSubmit(onSubmit)}
              className="flex-1"
              variant="crew"
              disabled={isPending}
            >
              {isPending ? 'Submitting...' : 'Submit'}
            </Button>
          </View>
        </CardContent>
      </Card>
    </Form>
  );
}

